// Vuetify dialoglarını başlığından tutup sürüklenebilir yapar; tüm dialoglar için tek delegasyon dinleyicisiyle çalışır.
const DRAGGING_CLASS = "dd-dragging";
const NO_TRANSITION_CLASS = "dd-no-transition";
const NO_DRAG_CLASS = "dd-no-drag";
const ACTIVE_HANDLE_CLASS = "dd-drag-active";
const EXPLICIT_HANDLE_SELECTOR = "[data-drag-handle]";
const TITLE_SELECTOR = ".v-card__title";
const TEXT_HANDLE_SELECTOR = ".v-card__text.headline";
const CARD_TEXT_CLASS = "v-card__text";
const NON_HANDLE_SELECTOR =
  ".v-card__actions, form, .v-form, .v-tabs, .v-window";
const IGNORE_SELECTOR =
  "button, a, input, textarea, select, .v-btn, .v-input, .v-tabs";
const CONTENT_SELECTOR = ".v-dialog__content";
const DIALOG_SELECTOR = ".v-dialog";
const CARD_SELECTOR = ".v-card";
const MIN_VIEWPORT_WIDTH = 600;
const EDGE_PADDING = 24;
const CLICK_THRESHOLD = 3;

const dialogStates = new WeakMap();
const movedDialogs = new Set();

let activeDrag = null;
let dragFrameId = 0;
let resizeFrameId = 0;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function applyOffset(state) {
  if (!state.x && !state.y) {
    state.contentEl.style.removeProperty("transform");
    return;
  }
  state.contentEl.style.transform =
    "translate3d(" + state.x + "px, " + state.y + "px, 0)";
}

function calculateBounds(state, rect) {
  return {
    minX: state.x + EDGE_PADDING - rect.right,
    maxX: state.x + window.innerWidth - EDGE_PADDING - rect.left,
    minY: state.y - rect.top,
    maxY: state.y + window.innerHeight - EDGE_PADDING - rect.top
  };
}

function resetState(state) {
  if (!state.x && !state.y) {
    return;
  }
  state.x = 0;
  state.y = 0;
  state.contentEl.classList.add(NO_TRANSITION_CLASS);
  applyOffset(state);
  movedDialogs.delete(state);
  window.requestAnimationFrame(function() {
    state.contentEl.classList.remove(NO_TRANSITION_CLASS);
  });
}

function clampToViewport(state) {
  const dialogEl = state.contentEl.querySelector(DIALOG_SELECTOR);
  if (!dialogEl) {
    return;
  }
  const bounds = calculateBounds(state, dialogEl.getBoundingClientRect());
  const x = clamp(state.x, bounds.minX, bounds.maxX);
  const y = clamp(state.y, bounds.minY, bounds.maxY);
  if (x !== state.x || y !== state.y) {
    state.x = x;
    state.y = y;
    applyOffset(state);
  }
}

function runViewportClamp() {
  resizeFrameId = 0;
  for (const state of movedDialogs) {
    if (!state.contentEl.isConnected) {
      state.observer.disconnect();
      movedDialogs.delete(state);
    } else if (state.x || state.y) {
      clampToViewport(state);
    }
  }
}

function onWindowResize() {
  if (resizeFrameId) {
    return;
  }
  resizeFrameId = window.requestAnimationFrame(runViewportClamp);
}

function getState(contentEl) {
  const existingState = dialogStates.get(contentEl);
  if (existingState) {
    return existingState;
  }
  const state = {
    contentEl: contentEl,
    x: 0,
    y: 0,
    wasActive: true,
    observer: null
  };
  state.observer = new window.MutationObserver(function() {
    const dialogEl = contentEl.querySelector(DIALOG_SELECTOR);
    const isActive =
      !!dialogEl && dialogEl.classList.contains("v-dialog--active");
    if (isActive && !state.wasActive) {
      resetState(state);
    }
    state.wasActive = isActive;
  });
  dialogStates.set(contentEl, state);
  return state;
}

function resolveHandle(dialogEl) {
  const explicitEl = dialogEl.querySelector(EXPLICIT_HANDLE_SELECTOR);
  if (explicitEl) {
    return { el: explicitEl, isFallback: false };
  }
  const cardEl = dialogEl.querySelector(CARD_SELECTOR);
  if (!cardEl) {
    return null;
  }
  // Başlık form gibi bir sarmalayıcı içinde olabilir; iç kartların başlığı tutamak sayılmaz.
  const titleEl = cardEl.querySelector(TITLE_SELECTOR);
  if (titleEl && titleEl.closest(CARD_SELECTOR) === cardEl) {
    return { el: titleEl, isFallback: false };
  }
  const firstEl = cardEl.firstElementChild;
  if (!firstEl || firstEl.matches(NON_HANDLE_SELECTOR)) {
    return null;
  }
  // Başlıksız onay dialoglarında başlık görevini headline sınıflı metin bloğu görür.
  if (
    firstEl.classList.contains(CARD_TEXT_CLASS) &&
    !firstEl.matches(TEXT_HANDLE_SELECTOR)
  ) {
    return null;
  }
  return { el: firstEl, isFallback: true };
}

function findDragTarget(event) {
  const contentEl = event.target.closest(CONTENT_SELECTOR);
  if (!contentEl) {
    return null;
  }
  const dialogEl = contentEl.querySelector(DIALOG_SELECTOR);
  if (!dialogEl || !dialogEl.contains(event.target)) {
    return null;
  }
  if (
    dialogEl.classList.contains("v-dialog--fullscreen") ||
    dialogEl.classList.contains(NO_DRAG_CLASS)
  ) {
    return null;
  }
  const handle = resolveHandle(dialogEl);
  if (!handle || !handle.el.contains(event.target)) {
    return null;
  }
  const ignoredEl = event.target.closest(IGNORE_SELECTOR);
  if (ignoredEl && handle.el.contains(ignoredEl)) {
    return null;
  }
  return {
    handleEl: handle.el,
    isFallback: handle.isFallback,
    contentEl: contentEl,
    dialogEl: dialogEl
  };
}

function renderFrame() {
  dragFrameId = 0;
  applyOffset(activeDrag.state);
}

function swallowClick(event) {
  event.stopPropagation();
  event.preventDefault();
}

function onPointerMove(event) {
  const drag = activeDrag;
  drag.state.x = clamp(
    drag.baseX + event.clientX - drag.startX,
    drag.bounds.minX,
    drag.bounds.maxX
  );
  drag.state.y = clamp(
    drag.baseY + event.clientY - drag.startY,
    drag.bounds.minY,
    drag.bounds.maxY
  );
  if (
    !drag.moved &&
    Math.abs(drag.state.x - drag.baseX) + Math.abs(drag.state.y - drag.baseY) >
      CLICK_THRESHOLD
  ) {
    drag.moved = true;
  }
  if (dragFrameId) {
    return;
  }
  dragFrameId = window.requestAnimationFrame(renderFrame);
}

function onPointerUp(event) {
  const drag = activeDrag;
  if (!drag) {
    return;
  }
  if (drag.handleEl.hasPointerCapture(drag.pointerId)) {
    drag.handleEl.releasePointerCapture(drag.pointerId);
  }
  drag.handleEl.removeEventListener("pointermove", onPointerMove);
  drag.handleEl.removeEventListener("pointerup", onPointerUp);
  drag.handleEl.removeEventListener("pointercancel", onPointerUp);
  if (dragFrameId) {
    window.cancelAnimationFrame(dragFrameId);
    dragFrameId = 0;
  }
  applyOffset(drag.state);
  drag.state.contentEl.classList.remove(DRAGGING_CLASS);
  drag.handleEl.classList.remove(ACTIVE_HANDLE_CLASS);
  if (drag.moved && event.type === "pointerup") {
    document.addEventListener("click", swallowClick, {
      capture: true,
      once: true
    });
  }
  activeDrag = null;
}

function onDocumentPointerDown(event) {
  document.removeEventListener("click", swallowClick, { capture: true });
  if (activeDrag || event.pointerType === "touch" || event.button !== 0) {
    return;
  }
  if (window.innerWidth < MIN_VIEWPORT_WIDTH) {
    return;
  }
  const target = findDragTarget(event);
  if (!target) {
    return;
  }
  const dialogRect = target.dialogEl.getBoundingClientRect();
  if (
    target.isFallback &&
    target.handleEl.getBoundingClientRect().height > dialogRect.height * 0.5
  ) {
    return;
  }
  const state = getState(target.contentEl);
  if (!movedDialogs.has(state)) {
    state.observer.observe(target.dialogEl, {
      attributes: true,
      attributeFilter: ["class"]
    });
    movedDialogs.add(state);
  }
  activeDrag = {
    state: state,
    handleEl: target.handleEl,
    pointerId: event.pointerId,
    startX: event.clientX,
    startY: event.clientY,
    baseX: state.x,
    baseY: state.y,
    bounds: calculateBounds(state, dialogRect),
    moved: false
  };
  target.handleEl.setPointerCapture(event.pointerId);
  target.handleEl.addEventListener("pointermove", onPointerMove, {
    passive: true
  });
  target.handleEl.addEventListener("pointerup", onPointerUp, { passive: true });
  target.handleEl.addEventListener("pointercancel", onPointerUp, {
    passive: true
  });
  target.contentEl.classList.add(DRAGGING_CLASS);
  target.handleEl.classList.add(ACTIVE_HANDLE_CLASS);
}

document.addEventListener("pointerdown", onDocumentPointerDown, {
  capture: true,
  passive: true
});
window.addEventListener("resize", onWindowResize, { passive: true });
